/**
 * Centralized file upload configuration for web client
 * These values mirror the backend validation library
 */

// =============================================================================
// DOCUMENT UPLOAD CONFIGURATION
// =============================================================================

/**
 * MIME types accepted for document uploads
 */
export const DOCUMENT_MIME_TYPES = [
  // Images - Standard formats
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  // Images - Apple formats
  'image/heic',
  'image/heif',

  // Documents - PDF
  'application/pdf',

  // Documents - Word
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

  // Spreadsheets - Excel
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

  // Presentations - PowerPoint
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',

  // Audio - Interview recordings, voice evidence
  'audio/mpeg', // MP3 files
  'audio/wav', // WAV files
  'audio/x-wav', // Alternative WAV MIME type
  'audio/ogg', // OGG Vorbis
  'audio/mp4', // M4A files (MPEG-4 audio)
  'audio/x-m4a', // Alternative M4A MIME type
] as const;

/**
 * File extensions for HTML accept attribute (document uploads)
 */
export const DOCUMENT_FILE_EXTENSIONS =
  '.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.tif,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp3,.wav,.ogg,.m4a';

/**
 * Combined accept string for document file inputs (MIME types + extensions)
 */
export const DOCUMENT_ACCEPT_STRING = [...DOCUMENT_MIME_TYPES, DOCUMENT_FILE_EXTENSIONS].join(',');

// =============================================================================
// PROFILE IMAGE CONFIGURATION
// =============================================================================

/**
 * MIME types accepted for profile images
 */
export const PROFILE_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;

/**
 * File extensions for HTML accept attribute (profile images)
 */
export const PROFILE_IMAGE_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp,.heic,.heif';

/**
 * Combined accept string for profile image inputs
 */
export const PROFILE_IMAGE_ACCEPT_STRING = [...PROFILE_IMAGE_MIME_TYPES, PROFILE_IMAGE_EXTENSIONS].join(',');

// =============================================================================
// FILE SIZE LIMITS
// =============================================================================

export const FILE_SIZE_LIMITS = {
  DOCUMENT_MAX_SIZE_MB: 50,
  DOCUMENT_MAX_SIZE_BYTES: 50 * 1024 * 1024,
  PROFILE_IMAGE_MAX_SIZE_MB: 5,
  PROFILE_IMAGE_MAX_SIZE_BYTES: 5 * 1024 * 1024,
} as const;

// =============================================================================
// VALIDATION HELPERS
// =============================================================================

export function isValidDocumentMimeType(mimeType: string): boolean {
  return DOCUMENT_MIME_TYPES.includes(mimeType as (typeof DOCUMENT_MIME_TYPES)[number]);
}

export function isValidProfileImageMimeType(mimeType: string): boolean {
  return PROFILE_IMAGE_MIME_TYPES.includes(mimeType as (typeof PROFILE_IMAGE_MIME_TYPES)[number]);
}

// =============================================================================
// USER-FRIENDLY ERROR MESSAGES
// =============================================================================

export const FILE_UPLOAD_ERRORS = {
  INVALID_DOCUMENT_TYPE:
    'File type not supported. Please upload: Images (JPG, PNG, GIF, WebP, BMP, TIFF, HEIC), Documents (PDF, DOC, DOCX), Spreadsheets (XLS, XLSX), Presentations (PPT, PPTX), or Audio (MP3, WAV, OGG, M4A).',
  INVALID_IMAGE_TYPE: 'Please upload an image file: JPG, PNG, GIF, WebP, or HEIC.',
  FILE_TOO_LARGE_DOCUMENT: `File size must be less than ${FILE_SIZE_LIMITS.DOCUMENT_MAX_SIZE_MB}MB.`,
  FILE_TOO_LARGE_PROFILE: `File size must be less than ${FILE_SIZE_LIMITS.PROFILE_IMAGE_MAX_SIZE_MB}MB.`,
} as const;

// =============================================================================
// USER-FRIENDLY TYPE LABELS
// =============================================================================

export const FILE_TYPE_LABELS = {
  IMAGES: 'JPG, PNG, GIF, WebP, BMP, TIFF, HEIC',
  DOCUMENTS: 'PDF, DOC, DOCX',
  SPREADSHEETS: 'XLS, XLSX',
  PRESENTATIONS: 'PPT, PPTX',
  AUDIO: 'MP3, WAV, OGG, M4A',
  ALL: 'Images, Documents, Spreadsheets, Presentations, Audio',
} as const;
